import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getAuthzenSubject } from './subject';
import { buildAuthZENAction } from './authz-operation-refs';

export type BatchAuthRequest = {
  key: string;
  resource: { type: string; id?: string; properties?: Record<string, any> };
  action: { name: string };
  context?: Record<string, any>;
};

type DecisionCacheValue = { allowed: boolean; ts: number };

export function useBatchAuthorization(requests: BatchAuthRequest[] | null | undefined) {
  const cacheRef = useRef<Map<string, DecisionCacheValue>>(new Map());
  const [decisions, setDecisions] = useState<Map<string, boolean> | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<any>(null);
  const lastResultsRef = useRef<Map<string, boolean> | null>(null);

  const effectiveRequests = useMemo(() => (Array.isArray(requests) ? requests : []), [requests]);

  const refresh = useCallback(async () => {
    let cancelled = false;
    // Provide a minimal cancellation path for callers/tests
    // Consumers can ignore the returned function
    const cancel = () => { cancelled = true; };
    if (!effectiveRequests.length) {
      if (!cancelled) setDecisions(new Map());
      return;
    }
    if (!cancelled) setLoading(true);
    if (!cancelled) setError(null);
    try {
      const now = Date.now();
      const results = new Map<string, boolean>();
      const pending: BatchAuthRequest[] = [];

      for (const r of effectiveRequests) {
        const c = cacheRef.current.get(r.key);
        if (c && now - c.ts < 60_000) {
          results.set(r.key, c.allowed);
        } else {
          pending.push(r);
        }
      }

      if (pending.length > 0) {
        const csrf = (document.cookie.match(/(?:^|; )_eid_csrf_v1=([^;]+)/)?.[1]) || '';
        let subject;
        try {
          subject = await getAuthzenSubject();
        } catch (err) {
          // FAIL CLOSED NOISILY: Identity governance requires known subject for all authz checks
          console.error('[IGA:AUTHZ] useBatchAuthorization: Subject resolution failed — failing closed for all pending checks.', err);
          for (const p of pending) {
            results.set(p.key, false);
          }
          subject = null;
        }
        if (subject) {
          const invalid: BatchAuthRequest[] = [];
          const valid: BatchAuthRequest[] = [];

          for (const p of pending) {
            const resourceType = p.resource?.type;
            const actionName = p.action?.name;
            if (typeof resourceType !== 'string' || resourceType.trim().length === 0) {
              invalid.push(p);
              continue;
            }
            if (typeof actionName !== 'string' || actionName.trim().length === 0) {
              invalid.push(p);
              continue;
            }
            valid.push(p);
          }

          for (const p of invalid) {
            results.set(p.key, false);
            cacheRef.current.set(p.key, { allowed: false, ts: now });
          }

          if (valid.length === 0) {
            // Nothing safe/valid to send to PDP.
            return;
          }

          const resp = await fetch('/access/v1/evaluations', {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-Token': csrf,
              'X-CSRFToken': csrf,
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({
              evaluations: valid.map(p => {
                const pdpApp =
                  (typeof p.context?.pdp_application === 'string' && p.context.pdp_application.trim().length > 0)
                    ? p.context.pdp_application
                    : (typeof p.resource?.properties?.pdp_application === 'string' && p.resource.properties.pdp_application.trim().length > 0)
                      ? p.resource.properties.pdp_application
                      : 'experience';

                const resourceType = p.resource.type;
                const actionName = p.action.name;

                return {
                  subject,
                  resource: {
                    type: resourceType,
                    id: p.resource?.id ?? '*',
                    properties: {
                      ...(p.resource?.properties || {}),
                      pdp_application: p.resource?.properties?.pdp_application || pdpApp,
                    },
                  },
                  action: buildAuthZENAction(resourceType, actionName),
                  context: { ...(p.context || {}), pdp_application: (p.context?.pdp_application || pdpApp) },
                };
              })
            })
          });
          const data = await resp.json().catch(() => ({} as any));
          const decisionsArr: Array<any> = (data?.results ?? data?.evaluations ?? data?.decisions ?? data ?? []) as any[];
          for (let i = 0; i < valid.length; i++) {
            const allow = decisionsArr[i]?.allowed ?? decisionsArr[i]?.allow ?? decisionsArr[i]?.decision ?? false;
            const allowed = allow === true || allow === 1 || allow === 'ALLOW';
            results.set(valid[i].key, allowed);
            cacheRef.current.set(valid[i].key, { allowed, ts: now });
          }
        }
      }

      // Only update state when results actually changed to prevent render loops
      if (!cancelled) {
        const prev = lastResultsRef.current;
        let changed = false;
        if (!prev || prev.size !== results.size) {
          changed = true;
        } else {
          for (const [k, v] of results.entries()) {
            if (prev.get(k) !== v) { changed = true; break; }
          }
        }
        if (changed) {
          lastResultsRef.current = results;
          setDecisions(results);
        }
      }
    } catch (e) {
      if (!cancelled) {
        setError(e);
        setDecisions(new Map()); // fail closed at call sites
      }
    } finally {
      if (!cancelled) setLoading(false);
    }
    return cancel;
  }, [effectiveRequests]);

  useEffect(() => {
    let cleanup: any;
    (async () => { cleanup = await refresh(); })();
    return () => { try { cleanup?.(); } catch {} };
  }, [refresh]);

  return { decisions, loading, error, refresh } as const;
}


